import React, { useState, useEffect } from 'react';
import { 
  Mail, Calendar, Users, Plus, Trash2, RefreshCw, Check, 
  AlertCircle, Loader2, Settings, Link2, ExternalLink,
  ChevronRight, Shield
} from 'lucide-react';

const SettingsAccounts = () => {
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(null);
  const [error, setError] = useState(null);
  const [newAccountName, setNewAccountName] = useState('');
  const [showConnectModal, setShowConnectModal] = useState(false);
  
  const userId = 'dwoodson';
  
  const getApiUrl = () => {
    if (typeof window !== 'undefined') {
      return `${window.location.protocol}//${window.location.host}`;
    }
    return process.env.REACT_APP_BACKEND_URL || '';
  };
  const apiUrl = getApiUrl();

  // Fetch connected accounts on mount
  useEffect(() => {
    fetchConnectedAccounts();
    
    // Check URL params for OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('nylas_connected') === 'true') {
      const email = urlParams.get('email');
      const provider = urlParams.get('provider');
      // Clear URL params
      window.history.replaceState({}, '', '/settings');
      // Refresh accounts list
      fetchConnectedAccounts();
      // Show success message
      setError(null);
    } else if (urlParams.get('nylas_error') === 'true') {
      const message = urlParams.get('message') || 'Failed to connect account';
      setError(message);
      window.history.replaceState({}, '', '/settings');
    }
  }, []);

  const fetchConnectedAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/api/nylas/connected-summary?user_id=${userId}`);
      const data = await response.json();
      
      if (data.accounts) {
        setConnectedAccounts(data.accounts);
      }
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Failed to load connected accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectAccount = () => {
    const accountName = newAccountName.trim() || 'My Account';
    setConnecting(true);
    // Redirect to Nylas OAuth with callback to /settings
    window.location.href = `${apiUrl}/api/nylas/auth/connect?user_id=${userId}&account_name=${encodeURIComponent(accountName)}&redirect_to=settings`;
  };

  const handleDisconnect = async (email) => {
    if (!window.confirm(`Are you sure you want to disconnect ${email}? This will remove access to email, calendar, and contacts for this account.`)) {
      return;
    }
    
    try {
      setDisconnecting(email);
      const response = await fetch(
        `${apiUrl}/api/nylas/disconnect?user_id=${userId}&email=${encodeURIComponent(email)}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        setConnectedAccounts(prev => prev.filter(acc => acc.email !== email));
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to disconnect account');
      }
    } catch (err) {
      setError('Failed to disconnect account');
    } finally {
      setDisconnecting(null);
    }
  };

  const getProviderIcon = (provider) => {
    switch (provider?.toLowerCase()) {
      case 'google':
        return (
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        );
      case 'microsoft':
      case 'outlook':
        return (
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#F25022" d="M1 1h10v10H1z"/>
            <path fill="#00A4EF" d="M1 13h10v10H1z"/>
            <path fill="#7FBA00" d="M13 1h10v10H13z"/>
            <path fill="#FFB900" d="M13 13h10v10H13z"/>
          </svg>
        );
      case 'yahoo':
        return (
          <div className="w-5 h-5 bg-purple-600 rounded flex items-center justify-center text-white text-xs font-bold">Y</div>
        );
      default:
        return <Mail className="w-5 h-5 text-gray-400" />;
    }
  };

  const getProviderName = (provider) => {
    switch (provider?.toLowerCase()) {
      case 'google': return 'Google';
      case 'microsoft': return 'Microsoft';
      case 'outlook': return 'Outlook';
      case 'yahoo': return 'Yahoo';
      case 'imap': return 'IMAP';
      default: return provider || 'Email';
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-4 md:p-8">
      {/* Header */}
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Settings className="w-8 h-8 text-purple-500" />
          <h1 className="text-2xl md:text-3xl font-bold">Connected Accounts</h1>
        </div>
        <p className="text-gray-400 mb-8">
          Connect your email accounts once to sync email, calendar, and contacts across your dashboard.
        </p>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <span className="text-red-400">{error}</span>
            <button 
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-300"
            >
              <Trash2 size={16} />
            </button>
          </div>
        )}

        {/* Connect New Account Card */}
        <div className="mb-8 p-6 bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/30 rounded-xl">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <Link2 className="w-5 h-5 text-purple-400" />
                Connect a New Account
              </h2>
              <p className="text-gray-400 text-sm mb-4">
                Add Gmail, Outlook, Yahoo, or any IMAP email account. One connection gives you access to:
              </p>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2 text-gray-300">
                  <Mail className="w-4 h-4 text-blue-400" />
                  <span>Email</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <Calendar className="w-4 h-4 text-green-400" />
                  <span>Calendar</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <Users className="w-4 h-4 text-orange-400" />
                  <span>Contacts</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowConnectModal(true)}
              disabled={connecting}
              className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 whitespace-nowrap"
              data-testid="connect-account-btn"
            >
              {connecting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Plus className="w-5 h-5" />
              )}
              Connect Account
            </button>
          </div>
        </div>

        {/* Connected Accounts List */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-300 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Your Connected Accounts ({connectedAccounts.length})
          </h2>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
            </div>
          ) : connectedAccounts.length === 0 ? (
            <div className="text-center py-12 bg-gray-900/50 rounded-xl border border-gray-800">
              <Mail className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-2">No accounts connected yet</p>
              <p className="text-gray-500 text-sm">
                Click "Connect Account" above to get started
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {connectedAccounts.map((account) => (
                <div
                  key={account.email}
                  className="p-4 bg-gray-900 border border-gray-800 rounded-xl hover:border-gray-700 transition-colors"
                  data-testid={`account-card-${account.email}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Provider Icon */}
                      <div className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center">
                        {getProviderIcon(account.provider)}
                      </div>
                      
                      {/* Account Info */}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{account.account_name}</span>
                          <span className="text-xs px-2 py-0.5 bg-gray-800 rounded text-gray-400">
                            {getProviderName(account.provider)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400">{account.email}</p>
                        {account.connected_at && (
                          <p className="text-xs text-gray-500 mt-1">
                            Connected {new Date(account.connected_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Services & Actions */}
                    <div className="flex items-center gap-6">
                      {/* Active Services */}
                      <div className="flex items-center gap-3">
                        <div 
                          className={`flex items-center gap-1.5 px-2 py-1 rounded ${
                            account.services?.email 
                              ? 'bg-blue-500/10 text-blue-400' 
                              : 'bg-gray-800 text-gray-500'
                          }`}
                          title="Email"
                        >
                          <Mail size={14} />
                          <span className="text-xs">Email</span>
                        </div>
                        <div 
                          className={`flex items-center gap-1.5 px-2 py-1 rounded ${
                            account.services?.calendar 
                              ? 'bg-green-500/10 text-green-400' 
                              : 'bg-gray-800 text-gray-500'
                          }`}
                          title="Calendar"
                        >
                          <Calendar size={14} />
                          <span className="text-xs">Calendar</span>
                        </div>
                        <div 
                          className={`flex items-center gap-1.5 px-2 py-1 rounded ${
                            account.services?.contacts 
                              ? 'bg-orange-500/10 text-orange-400' 
                              : 'bg-gray-800 text-gray-500'
                          }`}
                          title="Contacts"
                        >
                          <Users size={14} />
                          <span className="text-xs">Contacts</span>
                        </div>
                      </div>

                      {/* Disconnect Button */}
                      <button
                        onClick={() => handleDisconnect(account.email)}
                        disabled={disconnecting === account.email}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Disconnect account"
                        data-testid={`disconnect-${account.email}`}
                      >
                        {disconnecting === account.email ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <Trash2 size={18} />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Quick Links */}
                  <div className="mt-4 pt-4 border-t border-gray-800 flex flex-wrap gap-3">
                    <a 
                      href="/communications" 
                      className="flex items-center gap-2 text-sm text-gray-400 hover:text-blue-400 transition-colors"
                    >
                      <Mail size={14} />
                      View Emails
                      <ChevronRight size={14} />
                    </a>
                    <a 
                      href="/calendar" 
                      className="flex items-center gap-2 text-sm text-gray-400 hover:text-green-400 transition-colors"
                    >
                      <Calendar size={14} />
                      View Calendar
                      <ChevronRight size={14} />
                    </a>
                    <a 
                      href="/contacts" 
                      className="flex items-center gap-2 text-sm text-gray-400 hover:text-orange-400 transition-colors"
                    >
                      <Users size={14} />
                      View Contacts
                      <ChevronRight size={14} />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="mt-12 p-6 bg-gray-900/50 rounded-xl border border-gray-800">
          <h3 className="font-medium mb-3 flex items-center gap-2">
            <Shield className="w-5 h-5 text-green-500" />
            Your Data is Secure
          </h3>
          <ul className="space-y-2 text-sm text-gray-400">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span>We use OAuth 2.0 - your passwords are never stored</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span>You can disconnect accounts at any time</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span>Data is encrypted in transit and at rest</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Connect Account Modal */}
      {showConnectModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl border border-gray-700 max-w-md w-full p-6">
            <h2 className="text-xl font-semibold mb-4">Connect Email Account</h2>
            <p className="text-gray-400 text-sm mb-6">
              Connect any email provider to sync email, calendar, and contacts.
            </p>
            
            {/* Account Name Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Account Name (optional)
              </label>
              <input
                type="text"
                value={newAccountName}
                onChange={(e) => setNewAccountName(e.target.value)}
                placeholder="e.g., Work, Personal, Business"
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                data-testid="account-name-input"
              />
              <p className="text-xs text-gray-500 mt-2">
                This name will appear as the tab label in Email, Calendar, and Contacts.
              </p>
            </div>

            {/* Supported Providers */}
            <div className="mb-6 p-4 bg-gray-800/50 rounded-lg">
              <p className="text-xs text-gray-400 mb-3">Supported Providers:</p>
              <div className="flex flex-wrap gap-3">
                {['Gmail', 'Outlook', 'Yahoo', 'iCloud', 'IMAP'].map((provider) => (
                  <span 
                    key={provider}
                    className="px-3 py-1 bg-gray-700 rounded-full text-xs text-gray-300"
                  >
                    {provider}
                  </span>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowConnectModal(false)}
                className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConnectAccount}
                disabled={connecting}
                className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="connect-modal-btn"
              >
                {connecting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <ExternalLink size={18} />
                    Connect
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsAccounts;
